/**
 * Chat (Operations M2). Lists and badges come from `GET /chat/channels`; the realtime socket
 * (app/realtime.tsx) invalidates it on `chat.activity` / `chat.channels` / `chat.read`, so
 * nothing here polls. Message panes keep their own list (features/chat/useMessages.ts).
 */
import {
  ChatAdminChannelsSchema,
  ChatChannelDetailSchema,
  ChatChannelSummarySchema,
  ChatCreatedSchema,
  ChatMessageSchema,
  ChatMessagesResponseSchema,
  ChatOkSchema,
  ChatOverviewSchema,
  ChatPeopleResponseSchema,
  ChatReadResponseSchema,
  ChatRecordThreadSchema,
  ChatSearchResponseSchema,
  ChatUploadResponseSchema,
  type ChatAdhocWrite,
  type ChatRecordType,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from './client';

const enc = encodeURIComponent;

export const chatKeys = {
  all: ['chat'] as const,
  overview: ['chat', 'overview'] as const,
  channel: (id: string) => ['chat', 'channel', id] as const,
  record: (type: ChatRecordType, id: string) => ['chat', 'record', type, id] as const,
  people: (q: string) => ['chat', 'people', q] as const,
  search: (q: string) => ['chat', 'search', q] as const,
  admin: (q: string) => ['chat', 'admin', q] as const,
};

export const getChatOverview = () => apiGet('/chat/channels', ChatOverviewSchema);
export const getChannel = (id: string) =>
  apiGet(`/chat/channels/${enc(id)}`, ChatChannelDetailSchema);
export const getMessages = (
  id: string,
  q: { before?: number | undefined; after?: number | undefined; limit?: number | undefined },
) => apiGet(`/chat/channels/${enc(id)}/messages`, ChatMessagesResponseSchema, q);
export const postChatMessage = (id: string, body: string, attachmentIds: string[]) =>
  apiPost(`/chat/channels/${enc(id)}/messages`, ChatMessageSchema, { body, attachmentIds });
export const editChatMessage = (id: string, body: string) =>
  apiPatch(`/chat/messages/${enc(id)}`, ChatMessageSchema, { body });
export const deleteChatMessage = (id: string) =>
  apiDelete(`/chat/messages/${enc(id)}`, ChatMessageSchema);
/** chat.messages.moderate: remove anyone's message with a reason (audited). */
export const moderateChatMessage = (id: string, reason: string) =>
  apiPost(`/chat/messages/${enc(id)}/moderate`, ChatMessageSchema, { reason });
export const markChannelRead = (id: string, seq: number) =>
  apiPost(`/chat/channels/${enc(id)}/read`, ChatReadResponseSchema, { seq });
export const uploadChatFile = (id: string, file: File) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiUpload(`/chat/channels/${enc(id)}/files`, ChatUploadResponseSchema, form);
};
export const getRecordThread = (type: ChatRecordType, id: string) =>
  apiGet(`/chat/records/${type}/${enc(id)}`, ChatRecordThreadSchema);
export const setFollow = (id: string, follow: boolean) =>
  apiPut(`/chat/channels/${enc(id)}/follow`, ChatOkSchema, { follow });

/** Image/PDF attachments are served by the file store, behind the channel's read check. */
export const fileUrl = (fileId: string, download = false) =>
  `/api/v1/files/${enc(fileId)}${download ? '?download=1' : ''}`;

export function useChatOverview(enabled = true) {
  return useQuery({
    queryKey: chatKeys.overview,
    queryFn: getChatOverview,
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useChannel(id: string | null) {
  return useQuery({
    queryKey: chatKeys.channel(id ?? ''),
    queryFn: () => getChannel(id!),
    enabled: !!id,
    staleTime: 30_000,
    retry: false,
  });
}

export function useRecordThread(type: ChatRecordType, id: string | null | undefined) {
  return useQuery({
    queryKey: chatKeys.record(type, id ?? ''),
    queryFn: () => getRecordThread(type, id!),
    enabled: !!id,
    staleTime: 30_000,
    retry: false,
  });
}

export function useDmCandidates(q: string, enabled: boolean) {
  return useQuery({
    queryKey: chatKeys.people(q),
    queryFn: () => apiGet('/chat/people', ChatPeopleResponseSchema, { q }),
    enabled,
    staleTime: 60_000,
  });
}

export function useChatSearch(q: string) {
  return useQuery({
    queryKey: chatKeys.search(q),
    queryFn: () => apiGet('/chat/search', ChatSearchResponseSchema, { q }),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });
}

export function useStartDm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) => apiPost('/chat/dms', ChatChannelSummarySchema, { personId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chatKeys.overview }),
  });
}

export function useFollow(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (follow: boolean) => setFollow(channelId, follow),
    onSettled: () => void qc.invalidateQueries({ queryKey: chatKeys.all }),
  });
}

// Admin -----------------------------------------------------------------------------------

export function useAdminChannels(q: string, enabled: boolean) {
  return useQuery({
    queryKey: chatKeys.admin(q),
    queryFn: () => apiGet('/chat/admin/channels', ChatAdminChannelsSchema, { q }),
    enabled,
  });
}

export function useCreateAdhoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (w: ChatAdhocWrite) => apiPost('/chat/admin/channels', ChatCreatedSchema, w),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chatKeys.all }),
  });
}

export function useUpdateAdhoc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (w: {
      name?: string;
      description?: string | null;
      addMemberIds?: string[];
      removeMemberIds?: string[];
    }) => apiPatch(`/chat/admin/channels/${enc(id)}`, ChatOkSchema, w),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chatKeys.all }),
  });
}

export function useArchiveAdhoc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archived: boolean) =>
      apiPost(`/chat/admin/channels/${enc(id)}/archive`, ChatOkSchema, { archived }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chatKeys.all }),
  });
}

export const exportUrl = (id: string) => `/api/v1/chat/admin/channels/${enc(id)}/export`;
